import dayjs, { Dayjs } from 'dayjs';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { auditLogResource } from '../store/modules/merchant/types';

const getValidationSchema = (requiredFields: string[]) => {
  return z
    .object({
      createdAfter: z.preprocess(
        (val) => (val ? dayjs(val as Dayjs).startOf('day') : null),
        z
          .instanceof(dayjs as unknown as typeof Dayjs)
          .optional()
          .nullish()
          .refine((val) => !requiredFields?.includes('createdAfter') || !!val, {
            message: 'Start date is required',
          }),
      ),
      createdBefore: z.preprocess(
        (val) => (val ? dayjs(val as Dayjs).endOf('day') : null),
        z
          .instanceof(dayjs as unknown as typeof Dayjs)
          .optional()
          .nullish()
          .refine((val) => !requiredFields?.includes('createdBefore') || !!val, {
            message: 'End date is required',
          }),
      ),
      resources: z
        .array(z.enum(auditLogResource))
        .refine((val) => !requiredFields?.includes('resources') || val.length > 0, {
          message: 'At least one resource is required',
        }),
    })
    .superRefine((data, ctx) => {
      if (
        data.createdAfter &&
        data.createdBefore &&
        data.createdAfter.isAfter(data.createdBefore)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Start date must be before end date',
          path: ['createdAfter'],
        });
      }
    });
};

type FormValues = z.infer<ReturnType<typeof getValidationSchema>>;

const emptyValues: FormValues = {
  createdAfter: null,
  createdBefore: null,
  resources: [],
};

interface Props {
  initialValues?: Partial<FormValues>;
  requiredFields?: (keyof FormValues)[];
}

export const useAuditLogFiltersForm = ({ initialValues, requiredFields }: Props) => {
  return useForm<FormValues>({
    resolver: zodResolver(getValidationSchema(requiredFields as string[])),
    defaultValues: { ...emptyValues, ...initialValues },
    reValidateMode: 'onChange',
  });
};

export type { FormValues as AuditLogFiltersFormValues };
export { emptyValues as auditLogFiltersEmptyValues };
